/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import type React from 'react';

import {
  type PolymorphicCommonProps,
  type Radius,
  type Spacing,
} from '../_common/types';
import { getRadius, getSpacing } from '../_common/utils';

type BaseBoxProps = {
  /**
   * @default tokens.space['4']
   */
  padding?: Spacing;
  /**
   * @default undefined
   */
  paddingBlockStart?: Spacing;
  /**
   * @default undefined
   */
  paddingBlockEnd?: Spacing;
  /**
   * @default undefined
   */
  paddingInline?: Spacing;
  /**
   * @default undefined
   */
  paddingInlineStart?: Spacing;
  /**
   * @default undefined
   */
  paddingInlineEnd?: Spacing;
  /**
   * @default undefined
   */
  borderRadius?: Radius;
  children?: React.ReactNode;
  style?: React.CSSProperties;
};

export const Box = <T extends React.ElementType = 'div'>({
  as = 'div' as T,
  padding,
  paddingInline,
  paddingInlineStart,
  paddingInlineEnd,
  paddingBlockEnd,
  paddingBlockStart,
  borderRadius,
  children,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, BaseBoxProps>) => {
  const Component: React.ElementType = as ?? 'div';

  const additionalStyle = {
    borderRadius: getRadius(borderRadius, 'borderRadius'),
    padding: getSpacing(padding, 'padding'),
    paddingBlockEnd: getSpacing(paddingBlockEnd, 'paddingBlockEnd'),
    paddingBlockStart: getSpacing(paddingBlockStart, 'paddingBlockStart'),
    paddingInline: getSpacing(paddingInline, 'paddingInline'),
    paddingInlineEnd: getSpacing(paddingInlineEnd, 'paddingInlineEnd'),
    paddingInlineStart: getSpacing(paddingInlineStart, 'paddingInlineStart'),
  };

  return (
    <Component
      ref={ref}
      style={{ ...additionalStyle, ...style }}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};
